import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { defaultReducers } from './defaultReducers';
import axios from 'axios';
import { addParticipantCoach, removeParticipantCoach } from './coachesSlice';
import store from '../general/store';
import { fetchSchedule, scheduleSliceActions } from './scheduleSlice';
import type { Skill } from './skillSlice';
import { applyRemoteParticipantsToState } from '../schedule/realtime/applyScheduleDelta';

export interface Address {
  id: number;
  address: string;
  addressTwo: string;
  city: string;
  state: string;
  zip: string;
  label: string;
  isDefault: boolean;
  googleMapUrl: string;
  skill: Skill;
}

export interface Participant {
  id: string;
  user: Participant;
  firstName: string;
  lastName: string;
  scheduledCoach: number;
  note: string;
  requiresMoDropOff: boolean;
  transportationType: any;
  currentEventType: any;
  status: string;
  addresses: Address[];
  addressOverride: Address | null;
  effectiveAddress: Address | null;
  adhocAddress: string | null;
  adhocAddressGoogleUrl: string | null;
}

export interface ParticipantState {
  loading: boolean;
  error: Error;
  list: string[];
  byId: { string: Participant };
}

const initialState: ParticipantState = {
  loading: false,
  error: null,
  list: [],
  byId: {},
};

const participants = createSlice({
  name: 'participants',
  initialState,
  reducers: {
    getStart: defaultReducers.getStart,
    getSuccess: defaultReducers.getSuccess,
    getFailure: defaultReducers.getFailure,
    normalizeList: defaultReducers.normalizeList,
    updateParticipant(state: ParticipantState, action: PayloadAction<any>) {
      state.loading = false;
      state.error = '';
      // The server is returning a compounded ID in some places. Let's check for both.
      if (state.byId[action.payload.id]) {
        Object.assign(state.byId[action.payload.id], action.payload);
      } else {
        let combined_id = action.payload.user + '_' + action.payload.schedule;
        if (state.byId[combined_id]) {
          // Preserve the compound ID used as the byId key — server returns integer PK
          // which would break lookups if it overwrites id
          const preservedId = state.byId[combined_id].id;
          Object.assign(state.byId[combined_id], action.payload);
          state.byId[combined_id].id = preservedId;
        }
      }
    },
    updateParticipantForAllSchedules(
      state: ParticipantState,
      action: PayloadAction<any>
    ) {
      state.loading = false;
      state.error = '';

      // The server is returning a compounded ID in some places. Let's check for both.
      if (state.byId[action.payload.id]) {
        Object.assign(state.byId[action.payload.id], action.payload);
      } else {
        const payloadArray = action.payload;
        for (let i = 0; i < payloadArray.length; i++) {
          let combined_id =
            payloadArray[i].user + '_' + payloadArray[i].schedule;
          if (state.byId[combined_id]) {
            // Preserve the compound ID used as the byId key — server returns integer PK
            // which would break lookups if it overwrites id
            const preservedId = state.byId[combined_id].id;
            Object.assign(state.byId[combined_id], payloadArray[i]);
            state.byId[combined_id].id = preservedId;
          }
        }
      }
    },
    /**
     * Apply socket schedule.delta participants. Preserves compound Redux keys.
     * Payload: array of camelCase participant objects from the live feed.
     * Implementation shared with applyScheduleDelta.applyRemoteParticipantsToState.
     */
    applyRemoteParticipants(
      state: ParticipantState,
      action: PayloadAction<any>
    ) {
      const list = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];
      applyRemoteParticipantsToState(state, list);
    },
  },
});

export const {
  getStart,
  getSuccess,
  normalizeList,
  getFailure,
  updateParticipant,
  updateParticipantForAllSchedules,
  applyRemoteParticipants,
} = participants.actions;

export const participantSliceActions = participants.actions;
export const participantsReducer = participants.reducer;

/**
 * Resolve Redux participant row by compound id or server pk.
 * Keep this in sync with updateParticipant's dual-id lookup.
 */
const getParticipantFromState = (state, participantId) => {
  if (participantId == null) return null;
  const byId = state.participants.byId || {};
  if (byId[participantId]) return byId[participantId];
  const asStr = String(participantId);
  if (byId[asStr]) return byId[asStr];
  // Scan for server pk match (rare)
  const keys = state.participants.list || [];
  for (let i = 0; i < keys.length; i++) {
    const row = byId[keys[i]];
    if (row && (row.pk === participantId || row.id === participantId)) {
      return row;
    }
  }
  return null;
};

/**
 * Suppress applying our own schedule.delta for a short window after a local PATCH.
 * Prevents optimistic UI → self-echo fight (card jumps back then snaps forward).
 */
let suppressSelfDeltaUntil = 0;
export const noteLocalParticipantMutation = () => {
  suppressSelfDeltaUntil = Date.now() + 2500;
};
export const shouldSuppressSelfDelta = () =>
  Date.now() < suppressSelfDeltaUntil;

const sendParticipant = (participantId, participant) => async (
  dispatch: any
) => {
  // Live feed replaces pre-mutation fetchBasicSchedule() (race-prone).
  const state = store.getState();
  const existing = getParticipantFromState(state, participantId);
  const body = Object.assign({}, participant);
  // Never pass through a stale token from callers
  delete body.expectedParticipantModifiedDate;
  delete body.expected_participant_modified_date;

  // Optimistic concurrency disabled for now (see live-schedule-socket.md).
  delete body.expectedParticipantModifiedDate;
  delete body.expected_participant_modified_date;

  // Mark local mutation so socket self-echo does not re-apply mid-animation
  noteLocalParticipantMutation();

  try {
    // No expand=~all — full nested payload was Object.assign'd onto Redux and
    // briefly corrupted scheduledCoach / id, causing a visual jump-back.
    const updatedParticipant = await axios.patch(
      `${
        process.env.REACT_APP_SERVER_URL || ''
      }/schedules/scheduled_participants/${participant.id}/`,
      body
    );
    dispatch(
      scheduleSliceActions.patchMeta({
        modifiedDate: updatedParticipant.data.scheduleModifiedDate,
        isOutdated: false,
      })
    );
    // Safe merge only (preserve compound id + nested user)
    if (updatedParticipant.data) {
      const data = updatedParticipant.data;
      const userId =
        data.userId != null
          ? data.userId
          : data.user != null && typeof data.user === 'object'
          ? data.user.id
          : data.user;
      const schedulePk =
        data.scheduleId != null
          ? data.scheduleId
          : data.schedule != null && typeof data.schedule === 'object'
          ? data.schedule.id
          : data.schedule;
      const compoundId =
        participantId && String(participantId).includes('_')
          ? String(participantId)
          : existing && existing.id && String(existing.id).includes('_')
          ? String(existing.id)
          : userId != null && schedulePk != null
          ? `${userId}_${schedulePk}`
          : participantId;

      const coachId =
        data.scheduledCoachId != null
          ? data.scheduledCoachId
          : data.scheduledCoach != null &&
            typeof data.scheduledCoach !== 'object'
          ? data.scheduledCoach
          : undefined;

      const safePatch = {
        id: compoundId,
        status: data.status,
        note: data.note,
        modifiedDate: data.modifiedDate || null,
        requiresMoDropOff: data.requiresMoDropOff,
        adhocAddress: data.adhocAddress,
        adhocAddressGoogleUrl: data.adhocAddressGoogleUrl,
        addressOverrideId: data.addressOverrideId,
      };
      if (coachId !== undefined) {
        safePatch.scheduledCoach = coachId;
      }
      dispatch(updateParticipant(safePatch));
      // Keep coach columns aligned with server (idempotent with optimistic update)
      if (coachId !== undefined) {
        dispatch(
          syncCoachMembership({
            participantId: compoundId,
            scheduledCoachId: coachId,
          })
        );
      }
    }
    noteLocalParticipantMutation(); // extend suppress through response apply
    return updatedParticipant.data;
  } catch (err) {
    // 409 — toast only; axios interceptor already shapes err.message cleanly
    const isConflict =
      (err && err.isConflict) ||
      (err && err.status === 409) ||
      (err && err.response && err.response.status === 409);
    if (isConflict) {
      message.warning(
        (err && err.message) ||
          'Someone else updated this participant. Refreshing the schedule.'
      );
      const conflict =
        (err && err.data) || (err && err.response && err.response.data);
      if (conflict && conflict.participant) {
        dispatch(
          participantSliceActions.applyRemoteParticipants([
            conflict.participant,
          ])
        );
      }
      if (participantId) {
        dispatch(updateParticipant({ id: participantId, modifiedDate: null }));
      }
      dispatch(fetchSchedule());
      // Mark so callers skip a second generic message.error via getFailure
      const conflictErr = Object.assign({}, err || {}, {
        isConflict: true,
        message:
          (err && err.message) ||
          'Someone else updated this participant. Refreshing the schedule.',
        silentFailure: true,
      });
      throw conflictErr;
    }
    throw err;
  }
};

/**
 * After a remote delta, re-sync which coach list holds this participant id.
 */
export const syncCoachMembership = (payload: {
  participantId: string,
  scheduledCoachId: ?number,
}) => (dispatch: any, getState: any) => {
  const { participantId, scheduledCoachId } = payload;
  const coaches = getState().coaches;
  coaches.list.forEach((coachId) => {
    const coach = coaches.byId[coachId];
    if (!coach || !coach.scheduledParticipants) return;
    const has = coach.scheduledParticipants.indexOf(participantId) !== -1;
    const shouldHave =
      scheduledCoachId != null && Number(coachId) === Number(scheduledCoachId);
    if (has && !shouldHave) {
      dispatch(removeParticipantCoach({ coachId, participantId }));
    } else if (!has && shouldHave) {
      dispatch(addParticipantCoach({ coachId, participantId }));
    }
  });
};

export const deleteParticipant = (
  participantId: string,
  coachId: number,
  status?: string,
  isMaster?: boolean
) => async (dispatch: any) => {
  let originalStatus;

  try {
    const state = store.getState();
    originalStatus = state.participants.byId[participantId].status;
    dispatch(getStart());
    // When removing a participant, set status to 'available' to exclude from scheduled reports
    let data = {
      id: participantId,
      scheduledCoach: null,
      status: 'available',
      isMaster: isMaster,
    };

    if (status) {
      data.status = status;
      dispatch(updateParticipant({ id: participantId, status: status }));
    } else {
      // Set status to 'available' when removing participant
      dispatch(updateParticipant({ id: participantId, status: 'available' }));
    }

    dispatch(updateParticipant({ scheduledCoach: null, id: participantId }));
    dispatch(removeParticipantCoach({ coachId, participantId }));
    await dispatch(sendParticipant(participantId, data));
  } catch (err) {
    dispatch(
      updateParticipant({
        id: participantId,
        status: originalStatus,
        scheduledCoach: coachId,
      })
    );

    dispatch(addParticipantCoach({ coachId: coachId, participantId }));
    dispatch(getFailure(err));
  }
};

export const deleteParticipantForAllSchedules = (
  participantId: string,
  coachId: number,
  status?: string,
  date?: Date,
  isMaster?: boolean,
  scheduleTypeIds?: number[]
) => async (dispatch: any) => {
  let originalStatus;
  try {
    const state = store.getState();
    originalStatus = state.participants.byId[participantId].status;
    let data = {
      id: participantId,
      scheduledCoach: null,
      date: date,
      isMaster: isMaster,
      status: status || 'available',
      scheduleTypeIds: scheduleTypeIds || null,
    };

    const statusToUse = status || 'available';
    // Optimistic update for current schedule type
    dispatch(
      updateParticipantForAllSchedules({
        scheduledCoach: null,
        id: participantId,
        status: statusToUse,
      })
    );
    dispatch(removeParticipantCoach({ coachId, participantId }));
    const result = await dispatch(
      sendParticipantForAllSchedules(participantId, data)
    );
    const { participants, skipped } = result;
    if (Array.isArray(participants)) {
      dispatch(updateParticipantForAllSchedules(participants));
    }
    return { participants, skipped, requestedTypeIds: scheduleTypeIds || [] };
  } catch (err) {
    dispatch(
      updateParticipant({
        id: participantId,
        status: originalStatus,
        scheduledCoach: coachId,
      })
    );
    dispatch(addParticipantCoach({ coachId: coachId, participantId }));
    dispatch(getFailure(err));
    throw err;
  }
};

export const patchParticipant = (participant: Participant) => async (
  dispatch: any
) => {
  try {
    dispatch(getStart());
    const updatedParticipant = await dispatch(
      sendParticipant(participant.id, participant)
    );
    dispatch(updateParticipant(updatedParticipant));
  } catch (err) {
    dispatch(getFailure(err));
  }
};

const sendParticipantForAllSchedules = (
  participantId,
  participant: Participant
) => async (dispatch: any) => {
  const updatedParticipants = await axios.patch(
    `${
      process.env.REACT_APP_SERVER_URL || ''
    }/schedules/scheduled_participants/${participant.id}/bulk_update_status/`,
    participant
  );
  // Wire shape after CamelCaseJSONRenderer:
  //   { participants: [...], skippedScheduleTypeIds: [...] }
  const responseData = updatedParticipants.data;
  const participants = responseData.participants;
  const skipped = responseData.skippedScheduleTypeIds || [];

  if (Array.isArray(participants) && participants.length > 0) {
    dispatch(
      scheduleSliceActions.patchMeta({
        modifiedDate: participants[0].scheduleModifiedDate,
        isOutdated: false,
      })
    );
  }
  return { participants, skipped };
};

// Must update state before request for drag and drop to work smoothly
export const prePatchParticipant = (participant: Participant) => async (
  dispatch: any
) => {
  try {
    dispatch(getStart());
    dispatch(updateParticipant(participant));
    await dispatch(sendParticipant(participant.id, participant));
  } catch (err) {
    dispatch(getFailure(err));
  }
};

export const moveParticipant = (
  participantId: string,
  oldCoachId: number,
  newCoachId: number,
  isMaster: Boolean
) => async (dispatch: any) => {
  try {
    // No getStart() — loading=true is unused for drag and can flash lists
    noteLocalParticipantMutation();
    dispatch(removeParticipantCoach({ coachId: oldCoachId, participantId }));
    dispatch(
      updateParticipant({ scheduledCoach: newCoachId, id: participantId })
    );
    dispatch(addParticipantCoach({ coachId: newCoachId, participantId }));
    await dispatch(
      sendParticipant(participantId, {
        id: participantId,
        scheduledCoach: newCoachId,
        isMaster: isMaster,
      })
    );
  } catch (err) {
    dispatch(removeParticipantCoach({ coachId: newCoachId, participantId }));
    dispatch(
      updateParticipant({ scheduledCoach: oldCoachId, id: participantId })
    );
    dispatch(addParticipantCoach({ coachId: oldCoachId, participantId }));
    if (!(err && (err.silentFailure || err.isConflict))) {
      dispatch(getFailure(err));
    }
  }
};

export const postParticipant = (
  participantId: string,
  coachId: number,
  status?: string,
  isMaster?: Boolean
) => async (dispatch: any) => {
  let originalStatus;
  try {
    const state = store.getState();
    originalStatus = state.participants.byId[participantId].status;
    noteLocalParticipantMutation();
    let data = {
      id: participantId,
      scheduledCoach: coachId,
      isMaster: isMaster,
    };
    if (status) {
      data.status = status;
      dispatch(updateParticipant({ id: participantId, status: status }));
    }
    dispatch(updateParticipant({ scheduledCoach: coachId, id: participantId }));
    dispatch(addParticipantCoach({ coachId, participantId }));
    await dispatch(sendParticipant(participantId, data));
  } catch (err) {
    dispatch(
      updateParticipant({
        id: participantId,
        status: originalStatus,
        scheduledCoach: null,
      })
    );
    dispatch(removeParticipantCoach({ coachId, participantId }));
    if (!(err && (err.silentFailure || err.isConflict))) {
      dispatch(getFailure(err));
    }
  }
};

export const nextState = (participantId: string) => async (dispatch: any) => {
  try {
    dispatch(getStart());

    const updatedParticipant = await axios.post(
      `${process.env.REACT_APP_SERVER_URL || ''}/schedules/next_state/`,
      { id: participantId }
    );
    dispatch(updateParticipant(updatedParticipant.data));
  } catch (err) {
    dispatch(getFailure(err));
  }
};

export const assignParticipantToCoachForSchedules = (
  participantId: string, // compound "userId_scheduleId"
  coachUserId: number,
  scheduleTypeIds: number[],
  date: string,
  isMaster: Boolean
) => async (dispatch: any) => {
  try {
    const res = await axios.patch(
      `${
        process.env.REACT_APP_SERVER_URL || ''
      }/schedules/scheduled_participants/${participantId}/bulk_assign_to_coach/`,
      {
        coach_user_id: coachUserId,
        schedule_type_ids: scheduleTypeIds,
        date,
        is_master: isMaster,
      }
    );
    dispatch(updateParticipantForAllSchedules(res.data.participants));
    return res.data;
  } catch (err) {
    dispatch(getFailure(err));
    throw err;
  }
};

export const getParticipantReport = async (
  date_from: String,
  date_to: String
) => {
  const report = await axios.get(
    `${
      process.env.REACT_APP_SERVER_URL || ''
    }/aerostar/reports/participant_by_day?date_from=${date_from}&date_to=${date_to}`
  );
  return report.data;
};
